import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { CurrencyModel } from '@root/entities/currency'
import { useNetworkLinks } from '@root/entities/network'
import { WalletAddressTruncated } from '@root/entities/wallet/ui/common/wallet-address-truncated'
import { PlatformUtil } from '@root/shared/core'
import { AmountFormatted } from '@root/shared/core/format'
import { CopyButton } from '@root/shared/ui-kit'

import type { Address } from '@root/entities/address/Address'
import type { Network } from '@root/entities/network'
import type BigNumber from 'bignumber.js'

type DevTokenWalletInfoProps = {
  walletAddress: Address
  network: Network.Network
  amount?: BigNumber
}

export const DevTokenWalletInfo: React.FC<DevTokenWalletInfoProps> = ({
  walletAddress,
  amount,
  network,
}) => {
  const { buildContractLink } = useNetworkLinks()
  const nativeCurrency = CurrencyModel.nativeOf(network)

  const addressLink = useMemo(
    () => buildContractLink(walletAddress),
    [buildContractLink, walletAddress],
  )

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (addressLink) {
      PlatformUtil.openLink(addressLink, '_blank')
    }
  }

  return (
    <Root>
      <Link onClick={handleClick} href={addressLink}>
        <WalletAddressTruncated walletAddress={walletAddress} />
      </Link>
      <CopyButton size={14} value={walletAddress} />
      {amount ? (
        <Amount>
          <AmountFormatted amount={amount} /> {nativeCurrency.ticker}
        </Amount>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  gap: 2px;
  font-size: 12px;

  color: var(--text-11);
  font-weight: 600;
`

const Link = styled.a`
  color: var(--text-11);
  transition: color 0.15s linear;
  &:link,
  &:visited {
    color: var(--text-11);
    text-decoration: none;
  }
  &:hover {
    color: var(--text-12);
  }
`
const Amount = styled.span`
  color: var(--text-11);
  font-weight: 600;
`
